import { nonnull } from "@tento-core/assert";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Overlay } from "@tento-ui/components/overlay";
import { type SheetRow } from "@tento-lona";
import { type Option, RowId } from "@tento-lona/sheets";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaIcons, PencilIcon } from "@tento-ui/ui/icons";
import { Network } from "@tento-lona/network";
import type { LonaSdk } from "@tento-lona";
import { LookupKeyModal } from "./lookup-key-modal.ts";
import { TimescaleModal } from "./time-scale-modal.ts";

/**
 * The `sheet-row-footer` element: a row's footer with its timescale and
 * lookup key.
 */
@component({
  name: "sheet-row-footer",
})
export class SheetRowFooter extends LonaWebComponent {
  #$root = this.hydrate("root");
  #$timescale = this.hydrate("time-scale");
  #$timescaleContainer = this.hydrate("time-scale-container");
  #$lookupKey = this.hydrate("lookup-key");
  #$lookupKeyContainer = this.hydrate("lookup-key-container");
  #sdk: Option<LonaSdk.Client> = null;
  #row: Option<SheetRow.Joined.Handle> = null;

  bind(props: SheetRowFooter.Props): this {
    this.#sdk = props.sdk;
    this.#row = props.row;
    this.#updateContent(
      props.editable,
      props.onEditLookupKey,
      props.onEditTimescale,
    );
    this.#setupTooltips();
    return this;
  }

  /** Show the row's lookup key `s`; with none, an `"editable"` footer prompts for one. */
  bindLookupKey(s: Option<string>, access: "editable" | "readonly") {
    this.#$lookupKey.textContent = s ?? (access === "editable" ? "Add a key" : "");
  }

  bindTimescale(timescale: string) {
    this.#$timescale.textContent = timescale;
  }

  #updateContent(
    editable: boolean,
    onEditLookupKey?: (
      $footer: SheetRowFooter,
      newLookupKey: Option<string>,
    ) => Promise<Option<{ error: string }>>,
    onEditTimescale?: (
      $footer: SheetRowFooter,
      newTimescale: Network.TimeScaleCustom,
    ) => Promise<Option<{ error: string }>>,
  ): void {
    if (!this.#row) return;

    // Update the text content of the hydrated elements
    this.#$timescale.textContent = this.#row.timescaleSetting;
    this.bindLookupKey(this.#row.lookupKey, editable ? "editable" : "readonly");

    // Setup editable timescale if editable
    if (editable && onEditTimescale) {
      this.#$timescaleContainer.classList.add("editable");

      // Add pencil icon
      const $timescalePencilIcon = LonaIcons.make(PencilIcon, {
        sizePx: 10,
      });
      $timescalePencilIcon.id = "time-scale-pencil-icon";
      this.$("time-scale-icon-container").replaceChildren($timescalePencilIcon);

      // Add click handler to the container
      GESTURE_MANAGER.addPointerEvent(this.#$timescaleContainer, {
        onClick: () => this.#openTimescaleModal(onEditTimescale),
      });
    } else {
      this.#$timescaleContainer.classList.remove("editable");
      this.$("time-scale-icon-container").replaceChildren();
    }

    // Setup editable lookup key if editable
    if (editable && onEditLookupKey) {
      this.#$lookupKeyContainer.classList.add("editable");

      // Add pencil icon at the beginning (left side)
      const $pencilIcon = LonaIcons.make(PencilIcon, {
        sizePx: 10,
      });
      $pencilIcon.id = "pencil-icon";
      this.$("icon-container").replaceChildren($pencilIcon);

      // Add click handler to the container
      GESTURE_MANAGER.addPointerEvent(this.#$lookupKeyContainer, {
        onClick: () => this.#openLookupKeyModal(onEditLookupKey),
      });
    } else {
      this.#$lookupKeyContainer.classList.remove("editable");
      this.$("icon-container").replaceChildren();
    }
  }

  async #openLookupKeyModal(
    onEditLookupKey: (
      $footer: SheetRowFooter,
      newLookupKey: Option<string>,
    ) => Promise<Option<{ error: string }>>,
  ): Promise<void> {
    if (!this.#row) return;

    const rowId = RowId.fromLocalId(this.#row.id).asServerBacked() ?? null;
    const modal = LookupKeyModal.makeWith(
      nonnull(this.#sdk, "SheetRowFooter: editing before bind").rows,
      this.#row.lookupKey,
      rowId,
      this.#row.label,
      async (newLookupKey: Option<string>) => {
        return await onEditLookupKey(this, newLookupKey);
      },
    );

    Overlay.reveal(modal);
  }

  async #openTimescaleModal(
    onEditTimescale: (
      $footer: SheetRowFooter,
      newTimescale: Network.TimeScaleCustom,
    ) => Promise<Option<{ error: string }>>,
  ): Promise<void> {
    if (!this.#row) return;

    const currentTimescale = this.#row.timescaleSetting as Network.TimeScaleCustom;
    const modal = TimescaleModal.makeWith(
      currentTimescale,
      async (newTimescale: Network.TimeScaleCustom) => {
        return await onEditTimescale(this, newTimescale);
      },
    );

    Overlay.reveal(modal);
  }

  #setupTooltips(): void {
    const timescaleTooltip =
      "Time scale determines how data is grouped and displayed in this row";
    const lookupKeyTooltip =
      "Lookup key is used to reference this row in formulas and integrations";

    // Set up hover events for time scale
    this.#$timescale.addEventListener("pointerenter", () => {
      this.$("tooltip-content").textContent = timescaleTooltip;
      this.$("tooltip").style.opacity = "1";
    });
    this.#$timescale.addEventListener("pointerleave", () => {
      this.$("tooltip").style.opacity = "0";
    });

    // Set up hover events for lookup key
    this.#$lookupKey.addEventListener("pointerenter", () => {
      this.$("tooltip-content").textContent = lookupKeyTooltip;
      this.$("tooltip").style.opacity = "1";
    });
    this.#$lookupKey.addEventListener("pointerleave", () => {
      this.$("tooltip").style.opacity = "0";
    });
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        position: relative;
      }

      #root {
        color: var(--text-ter);
        padding: 10px 4px 4px 4px;
      }

      #time-scale-container.editable,
      #lookup-key-container.editable {
        cursor: pointer;
        padding: 4px 6px;
        margin: -4px -6px;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        transition: background-color 0.15s ease;
      }

      #time-scale-container.editable:hover,
      #lookup-key-container.editable:hover {
        background-color: var(--bg-hover);
      }

      #time-scale-pencil-icon,
      #pencil-icon {
        width: 12px;
        height: 12px;
        opacity: 0.6;
        flex-shrink: 0;
        transform: translateY(0px);
      }

      #tooltip {
        pointer-events: none;
        position: absolute;
        bottom: calc(100% + 0px);
        background: var(--fg-icon-pri);
        color: var(--text-pri-inv);
        border-radius: 8px;
        width: 100%;
        opacity: 0;
        padding: 12px;
      }
    `,
  ];

  static $html: HTMLTemplateElement = html`
    <div id=tooltip>
      <p id=tooltip-content class=small></p>
    </div>
    <std-row id="root">
      <std-flex id="time-scale-container">
        <div id="time-scale-icon-container"></div>
        <p id="time-scale" class="small"></p>
      </std-flex>
      <std-spacer></std-spacer>
      <std-flex id="lookup-key-container">
        <div id=icon-container></div>
        <p id="lookup-key" class="small"></p>
      </std-flex>
    </std-row>
  `;
}

/** Types that go with `SheetRowFooter`: `Props`. */
export namespace SheetRowFooter {
  export type Props = {
    sdk: LonaSdk.Client;
    row: SheetRow.Joined.Handle;
    editable: boolean;
    onEditLookupKey?: (
      $footer: SheetRowFooter,
      newLookupKey: Option<string>,
    ) => Promise<Option<{ error: string }>>;
    onEditTimescale?: (
      $footer: SheetRowFooter,
      newTimescale: Network.TimeScaleCustom,
    ) => Promise<Option<{ error: string }>>;
  };
}
